<template>
  <div class="bravado-cancelable-notice">
    <transition-group name="list" tag="div" class="bravado-cancelable-notice__transition-group">
      <div v-for="notice in notices" :key="notice.id" class="bravado-cancelable-notice__notice">
        <div class="bravado-cancelable-notice__notice-progress">
          <BravadoProgressChartV2
            class="bravado-cancelable-notice__notice-progress-icon"
            :percent="notice.progress"
            foreground-color="var(--theme-color-card)"
            background-color="var(--theme-color-card)"
            :background-opacity="0.3"
            :stroke-width="3"
            :size="32"
          />
          <BravadoText variant="subhead-semibold" class="bravado-cancelable-notice__notice-progress-counter">
            {{ notice.count }}
          </BravadoText>
        </div>

        <BravadoText variant="body-semibold" class="bravado-cancelable-notice__notice-text">
          {{ notice.message }}
        </BravadoText>

        <BravadoButtonV3
          v-if="!!notice.cancelActionTitle"
          class="bravado-cancelable-notice__notice-button"
          size="sm"
          variant="secondary"
          @click="handleCancel(notice)"
        >
          {{ notice.cancelActionTitle }}
        </BravadoButtonV3>
      </div>
    </transition-group>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeMount, onBeforeUnmount, ref } from '@nuxtjs/composition-api';
import BravadoButtonV3 from '@/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import BravadoProgressChartV2 from '@/components/common/BravadoProgressChartV2/BravadoProgressChartV2.vue';
import { uid } from '@/components/utils/core/uid';
import { events, CountDown, CancelableNoticeError } from '@/components/common/CancelableNotice/utils';
import { NOTICE_EVENT } from '@/components/common/CancelableNotice/constants';
import type { ICancelableNotice, INoticePayload } from '@/components/common/CancelableNotice/types';

const notices = ref<ICancelableNotice[]>([]);

function updateCounter(nextCount: number, noticeId: string): void {
  const noticeIndex = notices.value.findIndex(notice => notice.id === noticeId);

  if (noticeIndex === -1) return;

  notices.value[noticeIndex].count = nextCount;
}

function updateProgress(nextProgress: number, noticeId: string): void {
  const noticeIndex = notices.value.findIndex(notice => notice.id === noticeId);

  if (noticeIndex === -1) return;

  const notice = notices.value[noticeIndex];

  notice.progress = nextProgress;

  if (nextProgress === 100) {
    notice.resolve();
    notices.value.splice(noticeIndex, 1);
  }
}

function showNotice({ message, count = 3, cancelActionTitle, resolve, reject }: INoticePayload): void {
  if (!message) {
    reject(new CancelableNoticeError('Empty message provided'));
    return;
  }

  const id = uid();
  const countDown = new CountDown(count, id, updateCounter, updateProgress);

  notices.value.push({ id, message, cancelActionTitle, counter: countDown, count, progress: 0, resolve, reject });

  countDown.start();
}

function handleCancel(canceledNotice: ICancelableNotice): void {
  const noticeIndex = notices.value.findIndex(notice => notice.id === canceledNotice.id);

  canceledNotice.counter.stop();
  canceledNotice.reject(new CancelableNoticeError('Action has been cancelled'));

  notices.value.splice(noticeIndex, 1);
}

onBeforeMount(() => {
  events.$on(NOTICE_EVENT, showNotice);
});

onBeforeUnmount(() => {
  events.$off(NOTICE_EVENT, showNotice);
});
</script>

<style scoped lang="scss">
.bravado-cancelable-notice {
  position: fixed;
  z-index: 302;
  left: 50%;
  bottom: 88px;
  transform: translate(-50%);
  width: calc(100% - 32px);
  user-select: none;

  @include mq('sm') {
    max-width: 375px;
    bottom: 24px;
  }

  &__notice {
    box-shadow: var(--theme-elevation-15);
    display: flex;
    align-items: center;
    padding: 12px;
    color: var(--theme-color-card);
    background-color: var(--theme-color-text);
    border-radius: 16px;

    &:not(:first-child) {
      margin-top: 16px;
    }

    &-progress {
      @include flex-center;

      position: relative;
      margin-right: 12px;

      &-icon {
        @include absolute-center;
      }

      &-counter {
        padding: 6px 7px;
        min-width: 26px;
        text-align: center;
      }
    }

    &-text {
      @include multiline-overflow;

      margin-right: 12px;
    }

    &-button {
      margin-left: auto;
    }
  }

  .list-enter-active,
  .list-leave-active {
    transition: 0.2s ease-in-out;
    transition-property: transform, opacity;
  }

  .list-enter,
  .list-leave-to {
    opacity: 0;
    transform: translateY(90px);
  }
}
</style>
